// Pantalla de configuración — Formatos de Discogs permitidos (Req. 24.13–24.15).
// Solo accesible por propietarios (user_admin). Permite seleccionar qué formatos
// de Discogs mostrar en los resultados de búsqueda (checkboxes).

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../api/ApiContext.js';
import { AlertMessage, Card, Icon, PageHeader, Spinner } from '../../ui/components.js';
import { useAsync } from '../../ui/useAsync.js';

export function DiscogsFormatsView() {
  const { t } = useTranslation();
  const api = useApi();
  const formatsData = useAsync(() => api.config.getDiscogsFormats(), []);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Una vez cargados, inicializa el estado local.
  const options = formatsData.data?.options ?? [];
  const currentSelected = selected ?? formatsData.data?.selected ?? [];

  function toggle(fmt: string) {
    setSaved(false);
    setSelected((prev) => {
      const base = prev ?? formatsData.data?.selected ?? [];
      return base.includes(fmt) ? base.filter((f) => f !== fmt) : [...base, fmt];
    });
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await api.config.setDiscogsFormats(currentSelected);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('discogsFormats.errSave'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('discogsFormats.pageTitle')}
        subtitle={t('discogsFormats.pageSubtitle')}
        back
      />
      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        <Card title={t('discogsFormats.cardTitle')} icon="filter_list">
          <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
            {t('discogsFormats.cardHint')}
          </p>

          {formatsData.loading && <Spinner label={t('discogsFormats.loading')} />}
          {formatsData.error && (
            <p className="text-error font-body-sm text-body-sm">{String(formatsData.error)}</p>
          )}

          {!formatsData.loading && options.length > 0 && (
            <div className="grid grid-cols-2 gap-space-xs mb-space-md">
              {options.map((fmt: string) => {
                const checked = currentSelected.includes(fmt);
                return (
                  <label
                    key={fmt}
                    className={`flex items-center gap-space-xs px-space-sm h-10 rounded-lg border cursor-pointer transition-colors ${
                      checked
                        ? 'border-primary-container bg-primary-container/10 text-primary'
                        : 'border-border-subtle text-on-surface-variant hover:border-outline'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={checked}
                      onChange={() => toggle(fmt)}
                    />
                    <span className="font-body-sm text-body-sm">{fmt}</span>
                  </label>
                );
              })}
            </div>
          )}

          {currentSelected.length === 0 && !formatsData.loading && (
            <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-sm italic">
              {t('discogsFormats.noFilter')}
            </p>
          )}

          <AlertMessage message={saveError} className="mb-space-sm" />
          {saved && (
            <p className="text-goldmine-mint font-body-sm text-body-sm mb-space-sm">
              {t('discogsFormats.saved')}
            </p>
          )}

          <button
            className="btn-primary w-full"
            type="button"
            disabled={saving || formatsData.loading}
            onClick={() => void save()}
          >
            <Icon name="save" className="text-[18px]" />
            {saving ? t('discogsFormats.saving') : t('discogsFormats.save')}
          </button>
        </Card>
      </div>
    </div>
  );
}
